import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getConsultantTransactionHistoryApi } from '../../services/consultantManagement';
import { toastMessage } from '../../utils/toastMessage';
import { dateFormat } from '../../utils/dateFormat';
import useFullPageLoader from '../../hooks/useFullPageLoader';
import ResponsivePagination from '../../components/ResponsivePagination';
import { user } from '../../routes/route';

const ConsultantTransactionListing = ({ consultantId, username, search, startDate, endDate }) => {
  const [transactionList, setTransactionList] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  const fetchTransactionList = async (page = 1) => {
    try {
      showLoader();
      const sendData = {
        page: page,
        limit: 10,
        consultantId: consultantId
      };
      if (search) {
        sendData.search = search;
      }
      if (startDate) {
        sendData.startDate = startDate;
      }
      if (endDate) {
        sendData.endDate = endDate;
      }
      
      const { data } = await getConsultantTransactionHistoryApi(sendData);
      
      if (data.code === 200) {
        setTransactionList(data?.result?.list || []);
        setTotal(data?.result?.total || 0);
        setActivePage(page);
      } else {
        setTransactionList([]);
        setTotal(0);
        toastMessage(data.message, "error", "transactionList");
      }
    } catch (error) {
      console.log("error", error);
      setTransactionList([]);
      setTotal(0);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (consultantId) {
      fetchTransactionList(1);
    }
  }, [consultantId, search, startDate, endDate]);

  const handlePageChange = (page) => {
    fetchTransactionList(page);
  };

  const getTransactionTypeColor = (transactionType) => {
    return transactionType === 0 ? 'text-success' : 'text-danger';
  };

  const getTransactionTypeText = (transactionType) => {
    return transactionType === 0 ? 'Credit' : 'Debit';
  };

  const getTransactionForText = (transactionFor) => {
    const transactionForMap = {
      'session_book': 'Session Booking',
      'consult': 'Consultation',
      'gift': 'Gift',
      'membership': 'Membership',
      'meditation': 'Meditation',
      'vastu': 'Vastu',
      'topup': 'Wallet Top-up',
      'refund': 'Refund',
      'wallet_deduct': 'Wallet Deduction',
      'wallet_add': 'Wallet Addition',
      'wallet_refund': 'Wallet Refund',
      'payout': 'Payout',
      'shopify': 'Shopify',
      'live': 'Live Session'
    };
    return transactionForMap[transactionFor] || transactionFor;
  };

  return (
    <>
      {loader}
      <div className="content-table" style={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh', minHeight: '560px' }}>
        <div style={{ overflow: 'auto', flex: '1 1 0', minHeight: '460px' }}>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Transaction Type</th>
                <th>Amount</th>
                <th>Purpose</th>
                <th>User</th>
              </tr>
            </thead>
            <tbody>
              {transactionList.length > 0 ? (
                transactionList.map((transaction, index) => {
                  const ud = Array.isArray(transaction?.userDetails) ? transaction.userDetails[0] : transaction?.userDetails;
                  const userName = ud?.name ?? transaction?.meta?.name;
                  const userDisplayName = userName ? (typeof userName === 'string' ? userName.split(' ')[0] : userName) : null;
                  const userDetailId = ud?._id != null ? String(ud._id) : null;
                  return (
                  <tr key={index}>
                    <td>{dateFormat(transaction.createdAt)}</td>
                    <td>
                          <span style={{ 
                            color: transaction?.transactionType === 0 ? '#4CAF50' : '#F44336',
                            fontWeight: 'bold'
                          }}>
                            {transaction?.transactionTypeText || "---"}
                          </span>
                        </td>
                        <td>
                      ₹{Number(transaction.coins).toFixed(2)}
                    </td>
                    <td>{getTransactionForText(transaction.transactionFor)}</td>
                    <td>
                      {userDisplayName ? (
                        userDetailId ? (
                          <Link
                            className="consultant-txn-user-link"
                            to={`${user.USER_DETAIL}/${userDetailId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {userDisplayName}
                          </Link>
                        ) : (
                          <div>{userDisplayName}</div>
                        )
                      ) : (
                        "---"
                      )}
                    </td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    <div className="py-4">
                      <p className="text-muted">No transactions found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {total > 0 && (
          <div className="mt-3 d-flex justify-content-end" style={{ flexShrink: 0 }}>
            <ResponsivePagination
              currentPage={activePage}
              totalPages={Math.ceil(total / 10)}
              onPageChange={handlePageChange}
              totalItems={total}
              itemsPerPage={10}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ConsultantTransactionListing;
